import {
  GetBalanceCommand,
  CheckEmailCommand,
  CheckEmailResponse,
  GetNearHoldingsCommand,
  GetNearHoldingsResponse,
  SendUserEmailVerificationCommand,
  SendTosConsentCommand,
  SendResetPasswordEmailCommand,
  Holding,
  HoldingView,
} from "@foodi/core"
import { UserBalance } from "@foodi/core/lib/domain/valueObjects/UserBalance"
import { AuthActionTypes } from "@modules/auth"
import { createReducer, ThunkResult } from "@redux"
import { Action } from "@redux/action"
import { displayToastError } from "@utils"

export interface AccountState {
  userBalance: UserBalance | null
  selectedHolding: Holding | null
  hasBadge: boolean | null
  successfullyAddedHolding: boolean
  isEditionMode: boolean
  editionHoldingView: HoldingView | null
}

/*************  Actions  ****************/

const ActionTypes = {
  SET_USER_BALANCE: "account/SET_USER_BALANCE",
  SET_CURRENT_STEP_INDEX: "account/SET_CURRENT_STEP_INDEX",
  SET_USER_SELECTED_HOLDING: "account/SET_USER_SELECTED_HOLDING",
  SET_HAS_BADGE: "account/SET_HAS_BADGE",
  SET_SUCCESSFULLY_ADDED_HOLDING: "account/SET_SUCCESSFULLY_ADDED_HOLDING",
  SET_IS_EDITION_MODE: "account/SET_IS_EDITION_MODE",
  SET_EDITION_HOLDING_VIEW: "account/SET_EDITION_HOLDING_VIEW",
  RESET_STATE: "account/RESET_STATE",
}

const ActionCreators = {
  setUserBalance: (
    userBalance: UserBalance | null
  ): Action<UserBalance | null> => ({
    type: ActionTypes.SET_USER_BALANCE,
    payload: userBalance,
  }),
  setSelectedHolding: (
    selectedHolding: Holding | null
  ): Action<Holding | null> => ({
    type: ActionTypes.SET_USER_SELECTED_HOLDING,
    payload: selectedHolding,
  }),
  setHasBadge: (hasBadge: boolean | null): Action<boolean | null> => ({
    type: ActionTypes.SET_HAS_BADGE,
    payload: hasBadge,
  }),
  setSuccessfullyAddedHolding: (
    successfullyAddedHolding: boolean
  ): Action<boolean> => ({
    type: ActionTypes.SET_SUCCESSFULLY_ADDED_HOLDING,
    payload: successfullyAddedHolding,
  }),
  setIsEditionMode: (isEditionMode: boolean): Action<boolean> => ({
    type: ActionTypes.SET_IS_EDITION_MODE,
    payload: isEditionMode,
  }),
  setEditionHoldingView: (
    editionHoldingView: HoldingView | null
  ): Action<HoldingView | null> => ({
    type: ActionTypes.SET_EDITION_HOLDING_VIEW,
    payload: editionHoldingView,
  }),
  resetState: (): Action<null> => ({
    type: "account/RESET_STATE",
    payload: null,
  }),
}

/*************  Side Effects, only if applicable ****************/
// e.g. thunks, epics, etc
const ThunkActionCreators = {
  getBalance: (params: GetBalanceCommand): ThunkResult<Promise<void>> => async (
    dispatch,
    getState,
    { getDependencies }
  ) => {
    try {
      const { getBalance } = getDependencies()
      const { balance, allowedBalance } = await getBalance.execute(params)
      dispatch(ActionCreators.setUserBalance({ balance, allowedBalance }))
    } catch (e) {
      // Balance unavailability is non-critical: fail silently instead of showing an
      // error toast to the user (the balance simply won't update). All callers already
      // absorb this rejection, so no popin is surfaced anywhere.
      return Promise.reject(e)
    }
  },
  checkEmail: (
    params: CheckEmailCommand
  ): ThunkResult<Promise<CheckEmailResponse>> => async (
    dispatch,
    getState,
    { getDependencies }
  ) => {
    try {
      const { checkEmail } = getDependencies()
      return checkEmail.execute(params)
    } catch (e) {
      displayToastError(dispatch)
      return Promise.reject(e)
    }
  },
  getNearHoldings: (
    params: GetNearHoldingsCommand
  ): ThunkResult<Promise<GetNearHoldingsResponse>> => async (
    dispatch,
    getState,
    { getDependencies }
  ) => {
    try {
      const { getNearHoldings } = getDependencies()
      return getNearHoldings.execute(params)
    } catch (e) {
      displayToastError(dispatch)
      return Promise.reject(e)
    }
  },
  sendUserEmailVerification: (
    params: SendUserEmailVerificationCommand
  ): ThunkResult<Promise<any>> => async (
    dispatch,
    getState,
    { getDependencies }
  ) => {
    try {
      const { sendUserEmailVerification } = getDependencies()
      return sendUserEmailVerification.execute(params)
    } catch (e) {
      displayToastError(dispatch)
      return Promise.reject(e)
    }
  },
  sendUserResetPasswordEmail: (
    params: SendResetPasswordEmailCommand
  ): ThunkResult<Promise<any>> => async (
    dispatch,
    getState,
    { getDependencies }
  ) => {
    try {
      const { sendResetPasswordEmail } = getDependencies()
      const config = {
        ...params,
        AUTH0_WEB_CLIENT_ID: window.config.AUTH0_WEB_CLIENT_ID,
        AUTH0_WEB_DOMAIN: window.config.AUTH0_WEB_DOMAIN,
        AUTH0_WEB_RESET_PASSWORD: window.config.AUTH0_WEB_RESET_PASSWORD,
        AUTH0_WEB_CONNECTION: window.config.AUTH0_WEB_CONNECTION,
      }
      return sendResetPasswordEmail.execute(config)
    } catch (e) {
      displayToastError(dispatch)
      return Promise.reject(e)
    }
  },
  sendTosConsent: (
    params: SendTosConsentCommand
  ): ThunkResult<Promise<any>> => async (dispatch, _, { getDependencies }) => {
    try {
      const { sendTosConsent, getUserProfile } = getDependencies()

      const result = await sendTosConsent.execute(params)
      const userInfo = await getUserProfile.executeWithoutCache()
      dispatch({ type: AuthActionTypes.SET_USER_INFO, payload: userInfo })

      return Promise.resolve(result)
    } catch (e) {
      displayToastError(dispatch)
      return Promise.reject(e)
    }
  },
}

/*************  Reducer  ****************/

const initialState: AccountState = {
  userBalance: null,
  selectedHolding: null,
  hasBadge: null,
  successfullyAddedHolding: false,
  isEditionMode: false,
  editionHoldingView: null,
}

const Reduction = {
  setUserInfo: (
    state: AccountState,
    { payload: userBalance }: Action<UserBalance>
  ): AccountState => ({
    ...state,
    userBalance,
  }),
  setSelectedHolding: (
    state: AccountState,
    { payload: selectedHolding }: Action<Holding | null>
  ): AccountState => ({
    ...state,
    selectedHolding,
  }),
  setHasBadge: (
    state: AccountState,
    { payload: hasBadge }: Action<boolean | null>
  ): AccountState => ({
    ...state,
    hasBadge,
  }),
  setSuccessfullyAddedHolding: (
    state: AccountState,
    { payload: successfullyAddedHolding }: Action<boolean>
  ): AccountState => ({
    ...state,
    successfullyAddedHolding,
  }),
  setIsEditionMode: (
    state: AccountState,
    { payload: isEditionMode }: Action<boolean>
  ): AccountState => ({
    ...state,
    isEditionMode,
  }),
  setEditionHoldingView: (
    state: AccountState,
    { payload: editionHoldingView }: Action<HoldingView | null>
  ): AccountState => ({
    ...state,
    editionHoldingView,
  }),
  resetState: (
    state: AccountState
  ): AccountState => ({
    ...initialState,
    selectedHolding: null,
    hasBadge: null,
    successfullyAddedHolding: false,
    isEditionMode: false,
    editionHoldingView: null,
  }),
}

const reducer = createReducer(initialState, {
  [ActionTypes.SET_USER_BALANCE]: Reduction.setUserInfo,
  [ActionTypes.SET_USER_SELECTED_HOLDING]: Reduction.setSelectedHolding,
  [ActionTypes.SET_HAS_BADGE]: Reduction.setHasBadge,
  [ActionTypes.SET_SUCCESSFULLY_ADDED_HOLDING]:
    Reduction.setSuccessfullyAddedHolding,
  [ActionTypes.SET_IS_EDITION_MODE]: Reduction.setIsEditionMode,
  [ActionTypes.SET_EDITION_HOLDING_VIEW]: Reduction.setEditionHoldingView,
  [ActionTypes.RESET_STATE]: Reduction.resetState,

})

export default reducer

export {
  reducer as AccountReducer,
  ActionTypes as AccountActionTypes,
  ActionCreators as AccountActions,
  ThunkActionCreators as AccountThunks,
}
